<template>
	<button :type="type" :disabled="disabled" :class="rootClasses">
		<!-- круглая icon-кнопка (бывш. ButtonIcon) -->
		<template v-if="iconOnly">
			<svg-icon v-if="icon" :name="icon" :size="resolvedIconSize" />
			<slot />
		</template>

		<!-- текстовая кнопка (бывш. ButtonComponent) -->
		<template v-else>
			<span v-if="text" :class="['btn-text', { 'btn-text-gradient': variant === 'gradient' }]">{{ text }}</span>
			<svg-icon
				v-if="icon"
				:name="icon"
				:size="resolvedIconSize"
				:class="[`btn-icon-${iconPosition}`, { 'btn-icon-big': variant === 'gradient' }]"
			/>
			<slot />
		</template>
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import SvgIcon from '@/components/SvgIcon.vue'

const props = withDefaults(
	defineProps<{
		type?: 'button' | 'submit' | 'reset'
		disabled?: boolean
		variant?: 'primary' | 'secondary' | 'gradient'
		text?: string
		icon?: string
		iconPosition?: 'left' | 'right'
		iconSize?: number
		iconOnly?: boolean
	}>(),
	{
		type: 'button',
		disabled: false,
		variant: 'primary',
		iconPosition: 'right',
		iconOnly: false
		// iconSize НЕ задаём по умолчанию — чтобы отличить «не передан» от явного значения
	}
)

// Легаси-имена классов сохранены: внешние :deep(.btn*) в ProfileView/DialogComponent/DialogCookies
// продолжают работать без правок.
// .btn вешаем ТОЛЬКО у текстовой кнопки (признак — есть text, как required-проп старого ButtonComponent).
// Голые <base-button> со слотом (ButtonSecret, ButtonReasoning и т.п.) не получают .btn — только сброс.
const rootClasses = computed(() => {
	if (props.iconOnly) return 'button-icon'
	if (props.text != null) {
		return ['btn', { 'btn-secondary': props.variant === 'secondary', 'btn-gradient': props.variant === 'gradient' }]
	}
	return []
})

// Прежняя авто-логика размера: text-варианты 24/40, icon-only — 16; проп iconSize перекрывает.
const resolvedIconSize = computed(() => {
	if (props.iconSize != null) return props.iconSize
	if (props.iconOnly) return 16
	return props.variant === 'gradient' ? 40 : 24
})
</script>

<style lang="scss" scoped>
/* Сброс на element-селекторе (низкая специфичность) — классы потребителей всегда выигрывают. */
button {
	font: inherit;
	border: none;
	background: none;
	cursor: pointer;
	&:disabled {
		cursor: default;
	}
}

/* ── текстовая кнопка (из ButtonComponent) ── */
.btn {
	@include flex-center;
	font: var(--font-subtitle);
	background-color: var(--action-primary);
	color: var(--text-inverse);
	border: none;
	width: 100%;
	padding: 15px 20px;
	border-radius: var(--radius-md);
	position: relative;
	cursor: pointer;
	transition: $transition-bg, $transition-color;
	&:hover {
		background-color: var(--action-primary-hover);
	}
	&:active {
		background-color: var(--action-primary-hover);
	}
	&-icon-right {
		position: absolute;
		right: 20px;
	}
	&-icon-left {
		position: relative;
		order: 0;
		margin-right: 12px;
	}
	&-icon-big {
		color: var(--text-primary);
		background-color: var(--background-primary);
		background-image: url('@/resources/images/ring_icon.png');
		background-size: contain;
		border-radius: var(--radius-full);
		width: 40px;
		height: 40px;
		padding: 8px;
		margin-right: 0;
		position: absolute;
		left: 8px;
	}
	&-text {
		order: 1;
	}
	&-text-gradient {
		padding-left: 20px;
	}
	&-secondary {
		background-color: var(--background-secondary);
		color: var(--text-brand);
		&:hover {
			background-color: var(--background-secondary-hover);
			color: var(--text-brand);
		}
		&:active {
			background-color: var(--background-brand-subtle);
			color: var(--text-brand);
		}
	}
	&-gradient {
		padding: 18px 20px;
		border-radius: var(--radius-full);
		box-shadow: var(--shadow-button);
		background: var(--gradient-brand);
	}
}

/* ── круглая icon-кнопка (из ButtonIcon) ── */
.button-icon {
	background-color: var(--background-primary);
	color: var(--text-primary);
	border-radius: 100%;
	width: 44px;
	min-width: 44px;
	height: 44px;
	border: none;
	@include flex-center;
	transition: $transition-color;
	cursor: pointer;
	&:active {
		color: var(--text-primary);
	}
	&:hover {
		color: var(--icon-hover);
	}
	&-dark-enable {
		background-color: var(--background-inverse);
		color: var(--text-inverse);
		&:active {
			color: var(--text-primary);
		}
		&:hover {
			color: var(--icon-hover);
		}
	}
	&-dark-disable {
		background-color: var(--background-muted);
		color: var(--text-inverse);
		cursor: default;
	}
}
</style>
